import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Input, Select, Tag, Space, Modal,
  Form, notification, Switch, Row, Col, DatePicker,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CalendarOutlined, SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

export default function SammelanList() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/sammelan`, { headers: auth() });
      if (r.data.status) setData(r.data.result || []);
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({ event_type: 'physical', target_audience: 'all', is_published: true });
    setEditing(null); setModal(true);
  };
  const openEdit = (row) => {
    form.setFieldsValue({
      ...row,
      event_date: row.event_date ? dayjs(row.event_date) : null,
      is_published: !!row.is_published,
    });
    setEditing(row); setModal(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = { ...values, event_date: values.event_date?.format('YYYY-MM-DD') };
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/admin/sammelan/${editing.id}`, payload, { headers: auth() });
        notification.success({ message: 'Updated', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/admin/sammelan`, payload, { headers: auth() });
        notification.success({ message: 'Event Created', placement: 'topRight' });
      }
      setModal(false); fetchData();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Remove this event?', okType: 'danger', okText: 'Remove',
      onOk: async () => {
        await axios.delete(`${baseUrl}/admin/sammelan/${id}`, { headers: auth() });
        fetchData();
      },
    });
  };

  const TYPE_COLOR = { physical: '#7a0000', virtual: '#7c3aed', hybrid: '#0891b2' };
  const filtered = data.filter(d =>
    !search || d.title?.toLowerCase().includes(search.toLowerCase()) ||
    d.location?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    { title: 'Title', dataIndex: 'title', ellipsis: true },
    { title: 'Date', dataIndex: 'event_date', width: 110, render: v => v?.slice(0, 10) || '—' },
    { title: 'Type', dataIndex: 'event_type', width: 90, render: v => <Tag color={TYPE_COLOR[v] || '#6b7280'}>{v}</Tag> },
    { title: 'Location', dataIndex: 'location', ellipsis: true, render: v => v || '—' },
    { title: 'Audience', dataIndex: 'target_audience', width: 90, render: v => <Tag>{v}</Tag> },
    { title: 'Published', dataIndex: 'is_published', width: 90, render: v => v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag> },
    {
      title: 'Actions', width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      <Card
        title={<><CalendarOutlined style={{ color: '#0891b2', marginRight: 8 }} />Sammelan Events Management</>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Event</Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 14 }}>
          <Input prefix={<SearchOutlined />} placeholder="Search title or location..." value={search}
            onChange={e => setSearch(e.target.value)} allowClear style={{ width: 300 }} />
        </div>
        <Table dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="small" />
      </Card>

      <Modal open={modal} title={editing ? 'Edit Event' : 'Add Event'}
        onCancel={() => setModal(false)} onOk={handleSave} confirmLoading={saving}
        width={680} okText={editing ? 'Update' : 'Create'}>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item name="title" label="Title (Hindi)" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="event_type" label="Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="physical">Physical</Option>
                  <Option value="virtual">Virtual</Option>
                  <Option value="hybrid">Hybrid</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="title_en" label="Title (English)">
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="event_date" label="Event Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="target_audience" label="Target Audience">
                <Select>
                  <Option value="all">All Members</Option>
                  <Option value="youth">Youth</Option>
                  <Option value="women">Women</Option>
                  <Option value="elders">Elders</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="location" label="Location / Venue">
            <Input />
          </Form.Item>
          <Form.Item name="virtual_link" label="Virtual Meeting Link">
            <Input placeholder="Zoom / Meet link..." />
          </Form.Item>
          <Form.Item name="description" label="Description (Hindi)">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="description_en" label="Description (English)">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="is_published" label="Published" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
